import { Box, Center } from "@chakra-ui/layout"
import { observer } from "mobx-react"
import { useRouter } from "next/router"
import React from "react"

import { useSWRForGetAuthRegister } from "~/api/auth/register/get"
import { PostAuthRegisterBody } from "~/api/auth/register/post"
import Icon from "~/components/atoms/Icon"
import { Container } from "~/components/layouts/Container"
import ActionPairButton from "~/components/molecules/ActionPairButton"
import Dialog from "~/components/molecules/Dialog"
import FeedbackMessage from "~/components/molecules/FeedbackMessage"
import { Color, SpacingSize } from "~/consts/style-mixins"
import { internalLink } from "~/services/helper"

import { HeaderForAuthRegister } from "../AuthRegisterPage/components/HeaderForAuthRegister"
import { ApplicationCategory } from "../AuthRegisterPage/sections/3-ApplicationCategory"
import { NewGradJobs } from "../AuthRegisterPage/sections/4-NewGradJobs"
import { LongInternship } from "../AuthRegisterPage/sections/5-LongInternship"
import { Experience } from "../AuthRegisterPage/sections/6-Experience"
import { AdditionalProfile } from "../AuthRegisterPage/sections/7-AdditionalProfile"
import * as Styled from "./styled"
import { useAuthRegisterCompletePage } from "./useAuthRegisterCompletePage"

export type AuthRegisterCompletePageProps = {
  onSubmitForMock?: (data: PostAuthRegisterBody) => void
}

export const AuthRegisterCompletePage: React.VFC<AuthRegisterCompletePageProps> = (props) => {
  const router = useRouter()
  const { data: formMasterData } = useSWRForGetAuthRegister()

  const {
    control,
    errorMessages,
    isLoading,
    isLoggedIn,
    isOpenDialog,
    isShownExperience,
    isShownLongInternship,
    isShownNewGradJobs,
    onBeforeSubmit,
    onNavigate,
    onSkip,
    onSubmit,
    setIsOpenDialog,
  } = useAuthRegisterCompletePage(props)

  if (formMasterData === undefined || isLoggedIn === undefined) {
    return <LoadingPage />
  }

  // formMasterDataが取得できなかった場合TOPへ遷移させる
  if ("error" in formMasterData) {
    router.push(`${process.env.INFRA_INTERN_URL}/long-internship`)
    return <LoadingPage />
  }

  // ログアウト中のアクセスをブロック
  if (isLoggedIn === false) {
    router.replace(internalLink("/auth/register"))
    return <LoadingPage />
  }

  return (
    <>
      <HeaderForAuthRegister />
      <Box bgColor={Color.BackgroundGray} pt={48} pb={32} as="form" autoComplete="off" onSubmit={onSubmit}>
        <Container size="Medium">
          <Box pt={10} pb={SpacingSize.M} textAlign="center">
            <Styled.Title type="SubHero">登録が完了しました</Styled.Title>
            <Styled.SubTitle type="Note">
              以下の項目も入力して
              <span>
                選考通過率や
                <br />
                スカウト受信率をアップ
              </span>
              させよう
            </Styled.SubTitle>
          </Box>

          {/* formのエラーがある時は表示 */}
          {errorMessages.length > 0 && (
            <Box mt={SpacingSize.M}>
              <FeedbackMessage type="attention" messages={errorMessages} />
            </Box>
          )}

          <Box mt={SpacingSize.M}>
            <AdditionalProfile formMasterData={formMasterData} control={control} />
          </Box>

          <Box mt={SpacingSize.M}>
            <ApplicationCategory control={control} />
          </Box>

          {/* NOTE: 以下3つのformにはそれぞれshouldUnregisterを付与することで、消えるときに該当の要素がundefinedになる */}
          {/* 新卒のチェックボックスを押している時のみ表示 */}
          {isShownNewGradJobs && (
            <Box mt={SpacingSize.M}>
              <NewGradJobs control={control} formMasterData={formMasterData} />
            </Box>
          )}

          {/* 長期インターンのチェックボックスを押している時のみ表示 */}
          {isShownLongInternship && (
            <Box mt={SpacingSize.M}>
              <LongInternship control={control} formMasterData={formMasterData} />
            </Box>
          )}

          {/* 新卒か長期インターンのチェックボックスを押している時のみ表示 */}
          {isShownExperience && (
            <Box mt={SpacingSize.M}>
              <Experience control={control} formMasterData={formMasterData} />
            </Box>
          )}

          <Box w="100%" mt={SpacingSize._2XL}>
            <ActionPairButton
              mainAction={{
                label: "保存する",
                action: onBeforeSubmit,
                type: "submit",
                isLoading,
                className: "gtm-save-btn-new-registration-additionally",
              }}
              subAction={{
                label: "スキップ",
                action: onSkip,
                className: "gtm-skip-btn-new-registration-additionally",
              }}
            />
          </Box>
        </Container>
      </Box>

      <Dialog
        open={isOpenDialog}
        heading="変更を保存しますか？"
        overlayAction={() => setIsOpenDialog(false)}
        mainAction={{
          label: "保存する",
          action: () => {
            setIsOpenDialog(false)
            onBeforeSubmit()
            onSubmit()
          },
        }}
        subAction={{
          label: "変更を破棄する",
          action: onNavigate,
        }}
        thirdAction={{
          label: "編集画面に戻る",
          action: () => setIsOpenDialog(false),
        }}
      >
        入力内容に変更があります。変更を保存してから次に進みますか？
      </Dialog>
    </>
  )
}

const LoadingPage = () => (
  <>
    <HeaderForAuthRegister />
    <Center p={60}>
      <Icon type="Loading" size={16} color={Color.Primary} className="isLoading" />
    </Center>
  </>
)

export default observer(AuthRegisterCompletePage)
